import { AutoCompleteOption } from '@modules/common/components/AutoCompleteOption';
import { AutoCompleteTextField } from '@modules/common/components/AutoCompleteTextField';
import { WisdomIcon } from '@modules/oracle/components/WisdomIcon';
import { useGenerateEqlAndApplyFilters } from '@modules/queryBuilder/hooks';
import { Option } from '@modules/queryBuilder/types';
import { ChevronLeftSharp } from '@mui/icons-material';
import {
  Autocomplete,
  AutocompleteCloseReason,
  Box,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';

interface AiFilterAutoCompleteProps {
  isMenuOpen?: boolean;
  onBackToAllSuggestions?: () => void;

  onClose?: (
    event: React.SyntheticEvent<Element, Event>,
    reason: AutocompleteCloseReason,
  ) => void;
}

export const AiFilterAutoComplete = ({
  isMenuOpen,
  onClose,
  onBackToAllSuggestions,
}: AiFilterAutoCompleteProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [generateEqlAndApplyFilters] = useGenerateEqlAndApplyFilters();

  const options = [
    {
      id: '',
      displayName: searchTerm,
      group: '',
    },
  ];

  const onValueChange = async (value: string | Option | null) => {
    if (!value || !searchTerm) {
      return;
    }

    await generateEqlAndApplyFilters(searchTerm);
  };

  return (
    <Autocomplete
      open={isMenuOpen}
      freeSolo
      fullWidth
      autoHighlight
      openOnFocus
      options={options}
      popupIcon={false}
      onClose={onClose}
      disableCloseOnSelect
      renderInput={(params) => (
        <AutoCompleteTextField
          {...params}
          placeholder="Write a prompt to generate filters"
          variant="outlined"
        />
      )}
      groupBy={(option) => ''}
      onInputChange={(event, value, reason) => {
        if (reason === 'input') {
          setSearchTerm(value);
        }
      }}
      renderOption={(props, option, { inputValue, selected }) => {
        return (
          <Stack direction="row" alignItems="center" key={option.id}>
            <AutoCompleteOption
              key={option.id}
              option={{
                id: inputValue,
                displayName: inputValue,
              }}
              baseProps={props}
              searchTerm={inputValue}
              selected={selected}
              renderLabelAvatar={() => (
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={{ pr: 1 }}
                  minWidth="100%"
                  spacing={1}
                >
                  <WisdomIcon color="primary" sx={{ fontSize: '1rem' }} />
                  <Box>Generate filter with Wisdom:</Box>
                </Stack>
              )}
            />
          </Stack>
        );
      }}
      renderGroup={(params) => {
        return (
          <Box component="li" key={params.group}>
            <Paper
              elevation={1}
              onClick={onBackToAllSuggestions}
              sx={{
                pl: 1,
                pr: 2,
                py: 1,
                cursor: 'pointer',
                position: 'sticky',
                top: 0,
              }}
            >
              <Typography color="text.secondary">
                <ChevronLeftSharp fontSize="medium" /> Back to: All search
                suggestions...
              </Typography>
            </Paper>
            <Box component="ul" p={0}>
              {params.children}
            </Box>
          </Box>
        );
      }}
      slotProps={{
        paper: {
          elevation: 8,
          sx: { borderTopLeftRadius: 0, borderTopRightRadius: 0 },
        },
      }}
      onChange={(eve, value) => {
        onValueChange(value ?? null);
      }}
      filterOptions={(options) => options}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.displayName
      }
    />
  );
};
